import React, { useEffect, useState } from "react";
import { FontStyles } from "../utils/font-styles.ts";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-solid.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "../components//base/carousel.tsx";
import { DividerWithTip } from "../components/divider/divider-with-tip.tsx";
import { focusStyles } from "../utils/focus-styles.ts";

const benefits = [
  "Access to Edinburgh and East Central Scotland's largest property network",
  "Expert advice from solicitor estate agents who know your local market inside out",
  "Detailed analysis of recent sold prices in your area",
  "Insights into market conditions affecting your property’s value",
  "Professional guidance on maximising your property’s potential",
];

const reviews = [
  {
    review: "Fantastic Support and Friendly service from Start to Finish.",
    name: "Jacob Hunter",
    stars: 5,
  },
  {
    review: "A good service all around. Very helpful and friendly.",
    name: "Gary Wells",
    stars: 5,
  },
  {
    review: "Helpful, reliable and efficient",
    name: "Eleanor Pugh",
    stars: 5,
  },
];

export const BenefitsReviews: React.FC = () => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const updateCarouselState = () => {
      setCurrentIndex(carouselApi.selectedScrollSnap());
      setTotalItems(carouselApi.scrollSnapList().length);
    };
    updateCarouselState();
    carouselApi.on("select", updateCarouselState);
    return () => {
      carouselApi.off("select", updateCarouselState); // Clean up on unmount
    };
  }, [carouselApi]);

  const scrollToIndex = (index: number) => {
    carouselApi?.scrollTo(index);
  };

  return (
    <div className={`flex flex-col lg:flex-row gap-7.5 md:gap-10 lg:gap-15 w-full max-w-espc-max-content mx-auto`}>
      <div className={"flex flex-col gap-3 md:gap-3.5 w-full lg:w-[55%]"}>
        <h2 className={`${FontStyles.h3} text-north-sea font-semibold`}>Benefits of an ESPC valuation</h2>
        <div className={`flex flex-col gap-8 md:gap-9 text-slate ${FontStyles.h6}`}>
          <p>
            When you choose ESPC for your property valuation, you benefit from our extensive local knowledge and market
            expertise.
          </p>
          <ul className={`list-inside text-slate fa-ul !ml-7.5`}>
            {benefits.map((benefit, index) => {
              return (
                <li className={index === benefits.length - 1 ? "mb-0" : "mb-4"}>
                  <span className={`fa-li`}>
                    <i className={`fa-regular fa-sharp fa-check-circle text-loch`}></i>
                  </span>
                  {benefit}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <DividerWithTip className="lg:hidden" />
      <div className={"flex flex-col gap-6 md:gap-6.5 w-full lg:w-[45%]"}>
        <h2 className={`${FontStyles.h3} text-north-sea font-semibold text-center`}>What our clients say</h2>
        <Carousel
          setApi={setCarouselApi}
          opts={{
            dragFree: false,
            loop: true,
            watchDrag: (_, event: MouseEvent | TouchEvent) => {
              return event.type === "touchstart";
            },
          }}
        >
          <div className={"flex gap-1.5 md:gap-2"}>
            <div className={"flex items-center"}>
              <CarouselPrevious className={"py-8 text-[20px]"} />
            </div>
            <CarouselContent>
              {reviews.map((review) => {
                return (
                  <CarouselItem>
                    <div className={"pb-2 pr-2 pl-2"}>
                      <div
                        className={
                          "flex flex-col p-6 gap-5 md:gap-6.5 bg-neutral-white shadow-espc rounded-lg border border-foggy-firth"
                        }
                      >
                        <div className={"flex justify-between"}>
                          <div className={"flex gap-1"}>
                            {[1, 2, 3, 4, 5].map((star) => {
                              return (
                                <i
                                  className={`fa-sharp ${review.stars >= star ? "fa-solid" : "fa-regular"} fa-star text-yellow leading-none text-h6 md:text-h5`}
                                />
                              );
                            })}
                          </div>
                          <img
                            className={`h-[22px] md:h-[26px]`}
                            src="/static/icons/google-social-icon-large.png"
                            alt="Google social icon"
                          />
                        </div>
                        <div className={`text-slate ${FontStyles["body-md"]}`}>{review.review}</div>
                        <div className={`${FontStyles["body-md"]} font-semibold text-slate !leading-none`}>
                          {review.name}
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <div className={"flex items-center"}>
              <CarouselNext className={"py-8 text-[20px]"} />
            </div>
          </div>
        </Carousel>
        <div className="bottom-4 left-0 right-0 flex justify-center space-x-2 z-20">
          {Array.from({ length: totalItems }).map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-3 h-3 rounded-full ${currentIndex === index ? "bg-loch" : "bg-foggy-firth"} ${focusStyles}`}
              aria-label={`Go to slide ${(index + 1).toString()}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
